// Pure helpers for station scope in the admin pages and the landing page.
// The server is the authority (backend/src/utils/scope.js); these mirror its
// rules only to drive the UI. Keep the two in step.

export const ADMIN_ROLES = ['super_admin', 'admin'];
// Roles that normally cover several stations. Only a hint in the editor: scope is data, not the role.
export const REGIONAL_ROLES = ['maintenance_manager'];

export const isAdminRole = (role) => ADMIN_ROLES.includes(role);

/** Leaving an admin role must not carry all-stations access along: reset to 'none'. */
export function modeAfterRoleChange(prevRole, nextRole, mode) {
  return isAdminRole(prevRole) && !isAdminRole(nextRole) ? 'none' : mode;
}

/** 'all', or the list of station codes a users-list row can see (empty = nothing). */
export function reachOf(user) {
  if (isAdminRole(user.role) || user.all_stations) return 'all';
  return Array.isArray(user.station_scope) ? user.station_scope : [];
}

export function canSeeStation(user, code) {
  const reach = reachOf(user);
  return reach === 'all' || reach.includes(code);
}

/** The editor mode a stored user is in. */
export function modeOf(user) {
  if (isAdminRole(user.role) || user.all_stations) return 'all';
  const n = (user.station_scope || []).length;
  if (n === 0) return 'none';
  return n === 1 ? 'home' : 'regional';
}

// Departments: BOH (back of house, apron/cargo) and FOH (front of house).
export const AREAS = ['BOH', 'FOH'];

/** The departments a users-list row covers. Admins always cover both; nothing valid stored means both. */
export function areasOf(user) {
  if (isAdminRole(user.role)) return [...AREAS];
  const list = Array.isArray(user.areas) ? user.areas : [];
  const kept = AREAS.filter((a) => list.includes(a));
  return kept.length ? kept : [...AREAS];
}

export const canSeeArea = (user, area) => areasOf(user).includes(area);

/** The editor choice a stored user is in: 'both', 'BOH' or 'FOH'. */
export function areaModeOf(user) {
  const list = areasOf(user);
  return list.length === 2 ? 'both' : list[0];
}

export const areasFromMode = (area) => (area === 'BOH' || area === 'FOH' ? [area] : [...AREAS]);

/** API fields for an editor draft { role, mode, home, scope, area }. */
export function toPayload({ role, mode, home, scope, area }) {
  const areas = isAdminRole(role) ? [...AREAS] : areasFromMode(area);
  if (isAdminRole(role) || mode === 'all') {
    return { home_station: home || null, station_scope: [], all_stations: true, areas };
  }
  if (mode === 'home') {
    return { home_station: home || null, station_scope: home ? [home] : [], all_stations: false, areas };
  }
  if (mode === 'regional') {
    const list = [...new Set([...(home ? [home] : []), ...scope])];
    return { home_station: home || null, station_scope: list, all_stations: false, areas };
  }
  return { home_station: null, station_scope: [], all_stations: false, areas };
}

/** A message when the draft cannot be saved, otherwise null. */
export function validateDraft({ role, mode, home, scope }) {
  if (isAdminRole(role) || mode === 'all' || mode === 'none') return null;
  if (!home) return 'Choose a home station.';
  if (mode === 'regional' && new Set([home, ...scope]).size < 2) {
    return 'Regional access needs at least two stations. Use Home station only for one.';
  }
  return null;
}

/** What the user will see, for the editor's summary line. */
export function summaryOf({ role, mode, home, scope }) {
  if (isAdminRole(role) || mode === 'all') return 'all airports and head office';
  if (mode === 'home') return home ? `${home} only` : 'nothing yet, choose a home station';
  if (mode === 'regional') {
    const list = [...new Set([...(home ? [home] : []), ...scope])];
    return list.length ? list.join(', ') : 'nothing yet, choose stations';
  }
  return 'nothing. No station data until a scope is set';
}

/** Users who would currently see nothing. */
export function missingScope(users) {
  return users.filter((u) => {
    const reach = reachOf(u);
    return reach !== 'all' && reach.length === 0;
  });
}

/** One line for the landing page from the auth user (camelCase). Empty when the backend predates scope. */
export function accessLineFromAuth(user) {
  if (!user || (user.allStations === undefined && user.stationScope === undefined)) return '';
  if (user.allStations) return 'Station access: all airports and head office';
  const list = user.stationScope || [];
  return list.length ? `Station access: ${list.join(', ')}` : 'No station assigned yet. Ask an administrator.';
}
